import { useContext } from 'react';
import { CookieConsentContext } from '../context/CookieConsentContext';

/**
 * Provides access to the global cookie consent state and controls.
 *
 * Must be called inside a component that is a descendant of
 * `CookieConsentProvider`.
 *
 * Returns:
 *   - `consentStatus` — `'pending' | 'accepted' | 'rejected'`
 *   - `acceptCookies()` — persists acceptance and loads analytics scripts
 *   - `rejectCookies()` — persists rejection; no scripts are loaded
 *
 * @throws {Error} When used outside of `CookieConsentProvider`.
 *
 * @example
 * ```tsx
 * import { useCookieConsent } from '@/lib/hooks/useCookieConsent';
 *
 * export default function CookieBanner() {
 *   const { consentStatus, acceptCookies, rejectCookies } = useCookieConsent();
 *
 *   if (consentStatus !== 'pending') return null;
 *
 *   return (
 *     <div>
 *       <p>We use cookies to improve your experience.</p>
 *       <button onClick={acceptCookies}>Accept</button>
 *       <button onClick={rejectCookies}>Reject</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useCookieConsent() {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error(
      'useCookieConsent must be used within CookieConsentProvider'
    );
  }
  return context;
}
